import { Switch, Route } from "wouter";
import type { ComponentType } from "react";

// wouter's Route `component` prop expects ComponentType<RouteComponentProps>.
// Our page components accept no props, so we type the map entries loosely to
// stay compatible with both param and non-param routes.
type RouteComponent = ComponentType<any>;

// Single source of truth for the app's route table. Both the client router
// (App.tsx) and the SSR router (entry-server.tsx) render this same list, so
// the two can never drift apart. Drift previously caused the server to render
// a 404 for routes the client knew about (e.g. /blog/article/:slug), which
// produced a hydration mismatch and a flash of "Not Found" before the real
// page appeared.
//
// Each side supplies its own component implementations: the client passes
// lazy()-wrapped components to preserve code-splitting, while the server
// passes eager imports so renderToString can render synchronously. The
// AppRouteComponents interface forces both sides to provide every component,
// turning a missing route into a compile error instead of a runtime 404.
export interface AppRouteComponents {
  Home: RouteComponent;
  Blog: RouteComponent;
  SoroArticle: RouteComponent;
  BlogPost: RouteComponent;
  LegalPage: RouteComponent;
  Contact: RouteComponent;
  CoverFinder: RouteComponent;
  JoinPanel: RouteComponent;
  CarInsurance: RouteComponent;
  TagPage: RouteComponent;
  DynamicPage: RouteComponent;
  Admin: RouteComponent;
  AdminCMS: RouteComponent;
  AdminData: RouteComponent;
  AdminCoverFinder: RouteComponent;
  AdminAnalytics: RouteComponent;
  AdminReports: RouteComponent;
  AdminAdvertising: RouteComponent;
  Login: RouteComponent;
  NotFound: RouteComponent;
}

export function AppRoutes(c: AppRouteComponents) {
  return (
    <Switch>
      <Route path="/" component={c.Home} />
      <Route path="/blog" component={c.Blog} />
      <Route path="/blog/article/:slug" component={c.SoroArticle} />
      <Route path="/blog/:slug" component={c.BlogPost} />
      <Route path="/privacy-policy" component={c.LegalPage} />
      <Route path="/terms-and-conditions" component={c.LegalPage} />
      <Route path="/contact" component={c.Contact} />
      <Route path="/cover-finder" component={c.CoverFinder} />
      <Route path="/join-our-panel" component={c.JoinPanel} />
      <Route path="/car-insurance" component={c.CarInsurance} />
      <Route path="/tag/:slug" component={c.TagPage} />
      <Route path="/page/:slug" component={c.DynamicPage} />
      <Route path="/admin" component={c.Admin} />
      <Route path="/cms" component={c.AdminCMS} />
      <Route path="/data" component={c.AdminData} />
      <Route path="/cover-finder-data" component={c.AdminCoverFinder} />
      <Route path="/analytics" component={c.AdminAnalytics} />
      <Route path="/reports" component={c.AdminReports} />
      <Route path="/advertising" component={c.AdminAdvertising} />
      <Route path="/login" component={c.Login} />
      <Route component={c.NotFound} />
    </Switch>
  );
}
